/**
 * Single-writer lease algebra. One chronology has one writer; the lease is an engineering
 * clock artifact only and can never advance the world's politics or reroll an outcome.
 */
export type LeaseRecord = Readonly<{
  chronologyId: string;
  holderId: string;
  fencingToken: number;
  expiresAtMs: number;
}>;

export type LeaseRequest = Readonly<{
  chronologyId: string;
  holderId: string;
  nowMs: number;
  ttlMs: number;
}>;

export type LeaseRefusal = Readonly<{
  granted: false;
  reason: 'held-by-other' | 'chronology-mismatch';
  chronologyId: string;
  holderId: string;
  expiresAtMs: number;
}>;

export type LeaseDecision = Readonly<{ granted: true; record: LeaseRecord }> | LeaseRefusal;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export function parseLease(input: unknown): LeaseRecord | null {
  if (input === undefined || input === null) return null;
  if (!isRecord(input)) throw new Error('The stored writer lease is unreadable');
  const { chronologyId, holderId, fencingToken, expiresAtMs } = input;
  if (typeof chronologyId !== 'string' || !chronologyId || typeof holderId !== 'string' || !holderId ||
      typeof fencingToken !== 'number' || !Number.isSafeInteger(fencingToken) || fencingToken < 1 ||
      typeof expiresAtMs !== 'number' || !Number.isSafeInteger(expiresAtMs)) {
    throw new Error('The stored writer lease is malformed');
  }
  return { chronologyId, holderId, fencingToken, expiresAtMs };
}

/** Atomic check-and-set: a fresh holder gets a higher fencing token, which retires every older writer. */
export function claimLease(current: LeaseRecord | null, request: LeaseRequest): LeaseDecision {
  if (!Number.isSafeInteger(request.ttlMs) || request.ttlMs < 1) throw new Error('A writer lease needs a positive lifetime');
  if (current && current.chronologyId !== request.chronologyId) {
    return { granted: false, reason: 'chronology-mismatch', chronologyId: current.chronologyId, holderId: current.holderId, expiresAtMs: current.expiresAtMs };
  }
  if (current && current.holderId !== request.holderId && current.expiresAtMs > request.nowMs) {
    return { granted: false, reason: 'held-by-other', chronologyId: current.chronologyId, holderId: current.holderId, expiresAtMs: current.expiresAtMs };
  }
  return {
    granted: true,
    record: {
      chronologyId: request.chronologyId,
      holderId: request.holderId,
      fencingToken: (current?.fencingToken ?? 0) + 1,
      expiresAtMs: request.nowMs + request.ttlMs,
    },
  };
}

/** Returns null when the lease is free; otherwise the record that must remain (unchanged if another holds it). */
export function releaseLease(current: LeaseRecord | null, holderId: string): LeaseRecord | null {
  if (!current || current.holderId !== holderId) return current;
  return null;
}

export function leaseRefusalMessage(refusal: LeaseRefusal): string {
  if (refusal.reason === 'chronology-mismatch') {
    return `This save belongs to another chronology (${refusal.chronologyId}); a canon chronology cannot be advanced here.`;
  }
  return `Another tab (${refusal.holderId}) is already advancing this run and holds the rolling save until ${new Date(refusal.expiresAtMs).toISOString()}. Close it before playing here.`;
}
